import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IFilterOption } from '@rankmi/rankmi-table/dist/interfaces/filterProps'

import { useIntegrationStatus } from 'hooks/useIntegrationStatus'
import { joinKeys } from 'helpers/translations.helper'
import { IMainState } from 'store/states'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import {
  MonitoringStatus,
  getTranslationKeyMonitoringStatus,
} from 'enum/monitoring-status.enum'
import { EnrolState, getTranslationEnrolStateKey } from 'enum'
import { IntegrationStatusEnumerate } from 'enum/integrationStatus.enum'
import { Course } from 'graphqlAPI'
import {
  EvaluationResultState,
  getTranslationKeyEvaluationResultState,
} from 'enum/evaluation-result-state.enum'
import { Columns } from '../constants'

interface Column extends IFilterOption {
  label: string
  type: string
  value: string
}
export const prefixTranslation = 'monitoring.course'

interface FiltersColumnsProps {
  isSynchronous: boolean
  course?: Course
}

export const useFiltersColumns = ({
  isSynchronous,
  course,
}: FiltersColumnsProps) => {
  const prefix = joinKeys(prefixTranslation, 'attributes')
  const prefixStateList = joinKeys(prefix, 'stateAdvanceList')
  const prefixStateEvaluationList = joinKeys(prefix, 'stateEvaluationList')
  const dispatch = useDispatch()
  const { allProfilesList, loading: profilesLoading } = useSelector(
    ({ lmsProfiles }: IMainState) => lmsProfiles
  )
  useEffect(() => {
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  const [filtersColumns, setFilterColumns] = useState<Column[]>([])
  const { t } = useTranslation()
  const integrationStatus = useIntegrationStatus()

  useEffect(() => {
    if (!profilesLoading) {
      const { integrationCourse } = { ...course }
      const provider = integrationCourse?.provider

      setFilterColumns([
        {
          label: t(`${prefix}.name`),
          value: Columns.name,
          type: 'string',
        },
        {
          label: t(`${prefix}.userLmsState`),
          value: Columns.userLmsActive,
          type: 'tagsList',
          list: [true, false].map((value) => ({
            value: String(value),
            label: t(`${prefix}.userLmsStateList.${value}`),
          })),
        },
        {
          label: t(`${prefix}.area`),
          value: Columns.area,
          type: 'string',
        },
        {
          label: t(`${prefix}.state`),
          value: Columns.advance,
          type: 'tagsList',
          list: [
            MonitoringStatus.Finish,
            MonitoringStatus.InProgress,
            MonitoringStatus.Pending,
          ].map((value) => {
            const key = getTranslationKeyMonitoringStatus(value)
            return {
              value: String(value),
              label: t(`${prefixStateList}.${key}`),
            }
          }),
        },
        {
          label: t(`${prefix}.lessons`),
          value: Columns.lessons,
          type: 'number',
        },
        {
          label: t(`${prefix}.required_profile`),
          value: Columns.requiredProfiles,
          type: 'tagsList',
          list: allProfilesList.map(
            ({ id: value, name: label, color }: any) => ({
              value,
              label,
              color,
            })
          ),
        },
        {
          label: t(`${prefix}.optional_profile`),
          value: Columns.optionalProfiles,
          type: 'tagsList',
          list: allProfilesList.map(
            ({ id: value, name: label, color }: any) => ({
              value,
              label,
              color,
            })
          ),
        },
        {
          label: t(`${prefix}.evaluationsState`),
          value: Columns.stateEvaluations,
          type: 'tagsList',
          list: [
            EvaluationResultState.Approved,
            EvaluationResultState.Pending,
            EvaluationResultState.Failed,
          ].map((value) => {
            const key = getTranslationKeyEvaluationResultState(value)
            return {
              value: String(value),
              label: t(`${prefixStateEvaluationList}.${key}`),
            }
          }),
        },
        ...((isSynchronous && [
          {
            label: t(`${prefix}.finalState`),
            value: Columns.finalState,
            type: 'tagsList',
            list: [
              EnrolState.Approved,
              EnrolState.Denied,
              EnrolState.Pending,
              EnrolState.Absent,
              EnrolState.ExcusedAbsence,
            ].map((value) => ({
              value: String(value),
              label: t(
                `${prefix}.finalStateList.${getTranslationEnrolStateKey(value)}`
              ),
            })),
          },
        ]) || [
          {
            label: t(`${prefix}.progress`),
            value: Columns.progress,
            type: 'number',
          },
        ]),
        ...((provider && [
          {
            label: t(`${prefix}.integrationStatus`),
            value: Columns.integrationStatus,
            type: 'tagsList',
            list: Object.keys(integrationStatus).map((key) => ({
              value: IntegrationStatusEnumerate[key],
              label: integrationStatus[key],
            })),
          },
        ]) ||
          []),
      ])
    }
  }, [
    allProfilesList,
    course,
    integrationStatus,
    isSynchronous,
    prefix,
    prefixStateEvaluationList,
    prefixStateList,
    profilesLoading,
    t,
  ])
  return filtersColumns
}
